import type { FormField } from '@features/ksp-actions/ActionForm';
import type {
  KspRdCondition,
  KspRdSet,
  KspRdState,
} from '@entities/ksp/types';

/** Даты выдачи РД как расчётные предпосылки (план КСП ред. 1.2, §3.3–3.5). */
export const STATE_LABEL: Record<KspRdState, string> = {
  confirmed: 'Подтверждена',
  conditional: 'Условно — по плановой выдаче',
  unknown: 'Не определена',
};
export const CONDITION_LABEL: Record<KspRdCondition, string> = {
  receipt: 'Получение РД',
  permit: 'Разрешение к производству',
};
export const SOURCE_LABEL: Record<string, string> = {
  engineer: 'дата инженера',
  pto_uploaded_at: 'кандидат ПТО: загрузка ревизии',
  pto_afc_stamp: 'кандидат ПТО: штамп «В производство работ»',
};
export const ROW_STATUS: Record<string, string> = {
  matched: 'Найден в реестре РД ПТО',
  expected: 'Ожидается — в реестре ПТО ещё нет',
  ambiguous: 'Неоднозначно: несколько документов ПТО',
  duplicate_code: 'Шифр повторяется в файле',
  date_invalid: 'Дата не распознана',
  code_missing: 'Нет шифра',
};
export const NOTE_LABEL: Record<string, string> = {
  basis_previous_revision: 'основание по прежней ревизии — новая ждёт подтверждения',
  current_revision_not_indexed: 'новая ревизия ещё не разобрана в ПТО',
  no_active_revision_in_pto: 'в ПТО нет действующей ревизии',
  document_deleted_in_pto: 'комплект помечен на удаление в ПТО',
  document_purged_in_pto: 'комплект удалён из реестра ПТО',
  expected_set_not_in_pto: 'комплекта ещё нет в реестре ПТО',
  partial_confirmation: 'подтверждена часть условий — недостающее по плановой выдаче',
  confirmation_pages_not_covering_work:
    'подтверждение относится к другим листам комплекта — нужна дата для листов этой работы',
};
export const RUN_STATUS: Record<string, string> = {
  queued: 'Пересчёт в очереди',
  processing: 'Пересчёт выполняется',
  completed: 'Пересчитано',
  stale: 'Результат устарел — ждёт пересчёта по новым данным',
  failed: 'Пересчёт не выполнен',
};
export const ru = (iso?: string | null) =>
  iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—';
export const revisionName = (r?: { label?: string | null; revision_no: number }) =>
  r ? (r.label ?? `рев. ${r.revision_no}`) : '';

export function setText(set: KspRdSet) {
  const revision = set.basis_revision ?? set.current_revision;
  const head = revision ? `${set.set_code} (${revisionName(revision)})` : set.set_code;
  const body =
    set.state === 'confirmed'
      ? (set.conditions ?? [])
          .map(
            (c) =>
              `${CONDITION_LABEL[c.kind].toLowerCase()} ${ru(c.date)}${c.confirmed_by_name ? ` (${c.confirmed_by_name})` : ''}`,
          )
          .join(', ')
      : set.state === 'conditional'
        ? `плановая выдача ${ru(set.planned_on)}`
        : 'дат нет';
  const notes = (set.notes ?? []).map((n) => NOTE_LABEL[n] ?? n);
  return `${head}: ${body}${notes.length ? ` — ${notes.join('; ')}` : ''}`;
}

export interface RdDatesTabProps {
  projectId: string;
  /** Версия проекта КСП (для загрузки файла). */
  version?: number;
  canEdit: boolean;
  execute: (path: string, body: Record<string, unknown>) => Promise<unknown>;
  open: (
    title: string,
    fields: FormField[],
    action: (values: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  onError: (error: unknown) => void;
  workOptions: { value: string; label: string }[];
}
